import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { entityService } from '../../../services/EntityService';
import { queryKeys } from '../../../lib/queryKeys';
import type { Entity } from '../../../types/Entity';
import type { CreateEntityRequest, UpdateEntityRequest } from '../../../types/Entity';
import { useAuth } from '@/hooks/useAuth';
import type { ListParams as EntityListParams } from '@/services/EntityService';

interface UseEntitiesParams extends EntityListParams {
  enabled?: boolean;
}

interface UseAllEntitiesParams extends Omit<EntityListParams, 'limit' | 'offset'> {
  enabled?: boolean;
}

export function useEntities(params: UseEntitiesParams) {
  const { isAuthenticated } = useAuth();
  const { enabled = true, ...listParams } = params;
  return useQuery({
    queryKey: queryKeys.entities.list(listParams.type, {
      limit: listParams.limit,
      offset: listParams.offset,
      order: listParams.order,
      filters: listParams.filters,
    }),
    queryFn: () => entityService.list(listParams),
    staleTime: 30_000,
    enabled: enabled && isAuthenticated,
  });
}

export function useAllEntities(params: UseAllEntitiesParams) {
  const { isAuthenticated } = useAuth();
  const { enabled = true, ...listParams } = params;
  return useQuery({
    queryKey: queryKeys.entities.list(listParams.type, {
      limit: 'all',
      order: listParams.order,
      filters: listParams.filters,
    }),
    queryFn: () => entityService.listAll(listParams),
    staleTime: 30_000,
    enabled: enabled && isAuthenticated,
  });
}

export function useEntity(id: string, type: string) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.entities.detail(type, id),
    queryFn: () => entityService.get({ type, id }),
    enabled: Boolean(type && id) && isAuthenticated,
    staleTime: 30_000,
  });
}

export function useCreateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEntityRequest) => entityService.create(input),
    onMutate: async (input: CreateEntityRequest) => {
      const type = input.entity_type;
      const listKey = queryKeys.entities.list(type);

      await queryClient.cancelQueries({ queryKey: listKey });
      const prev = queryClient.getQueryData(listKey);

      queryClient.setQueryData(listKey, (old: Entity[] | undefined) => [
        { ...input, id: `temp-${Date.now()}`, __optimistic: true },
        ...(old ?? []),
      ]);

      return { prev, listKey } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.listKey, ctx.prev);
      }
    },
    onSettled: (_data, _err, vars: CreateEntityRequest) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.list(vars.entity_type) });
    },
  });
}

export function useUpdateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, id, patch }: { type: string; id: string; patch: UpdateEntityRequest }) =>
      entityService.update({ type, id, patch }),
    onMutate: async ({ type, id, patch }) => {
      const detailKey = queryKeys.entities.detail(type, id);

      await queryClient.cancelQueries({ queryKey: detailKey });
      const prev = queryClient.getQueryData(detailKey);

      queryClient.setQueryData(detailKey, (old: Entity | undefined) => ({
        ...(old ?? {}),
        ...patch,
        __optimistic: true,
      }));

      return { prev, detailKey, type } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.detailKey, ctx.prev);
      }
    },
    onSettled: (_data, _err, { type, id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.detail(type, id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.list(type) });
    },
  });
}

export function useDeleteEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; type: string }) => entityService.remove({ id }),
    onSettled: (_data, _err, { type }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.list(type) });
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
    },
  });
}
